import { ReportAnalyticsRbacPermissions } from "./enums";

export type ReportType = "reviewReport" | "employeeReport" | "goalReport" | "surveyReport";
export type ReportsType = "reviewReports" | "employeeReports" | "goalReports" | "surveyReports";
export type ReportCSVType = "reviewReportsCSV" | "employeeReportsCSV" | "goalReportsCSV" | "surveyReportsCSV";
export type ReportSortType = "ReviewReportsSort" | "EmployeeReportsSort" | "GoalReportsSort" | "SurveyReportsSort";
export type ReportSummarizeByType =
	| "ReviewReportsSummarizeBy"
	| "EmployeeReportsSummarizeBy"
	| "GoalReportsSummarizeBy"
	| "SurveyReportsSummarizeBy";
export type ReportAggOperationsType =
	| "ReviewReportsAggregationOperation"
	| "EmployeeReportsAggregationOperation"
	| "GoalReportsAggregationOperation"
	| "SurveyReportsAggregationOperation";

export const REPORT_CONFIG: Record<
	ReportType,
	{
		queryName: string;
		reportsType: ReportsType;
		reportCSVType: ReportCSVType;
		sorts: ReportSortType;
		summarizeBy: ReportSummarizeByType;
		aggregationOperations: ReportAggOperationsType;
	}
> = {
	reviewReport: {
		queryName: "GetReviewReportData",
		reportsType: "reviewReports",
		reportCSVType: "reviewReportsCSV",
		sorts: "ReviewReportsSort",
		summarizeBy: "ReviewReportsSummarizeBy",
		aggregationOperations: "ReviewReportsAggregationOperation",
	},
	employeeReport: {
		queryName: "GetEmployeeReportData",
		reportsType: "employeeReports",
		reportCSVType: "employeeReportsCSV",
		sorts: "EmployeeReportsSort",
		summarizeBy: "EmployeeReportsSummarizeBy",
		aggregationOperations: "EmployeeReportsAggregationOperation",
	},
	goalReport: {
		queryName: "GetGoalReportData",
		reportsType: "goalReports",
		reportCSVType: "goalReportsCSV",
		sorts: "GoalReportsSort",
		summarizeBy: "GoalReportsSummarizeBy",
		aggregationOperations: "GoalReportsAggregationOperation",
	},
	surveyReport: {
		queryName: "GetSurveyReportData",
		reportsType: "surveyReports",
		reportCSVType: "surveyReportsCSV",
		sorts: "SurveyReportsSort",
		summarizeBy: "SurveyReportsSummarizeBy",
		aggregationOperations: "SurveyReportsAggregationOperation",
	},
};

const SHARED_WITH_POPULATIONS = ["managers", "contributors", "group_admins"] as const;

/** Honors sharing intent: narrows the requested populations to the ones the report is shared with, and returns
 * empty when it is shared with none of them so the caller's empty-subject guard rejects rather than widening. */
export const getSharedWithPopulations = (sharedWith: string[] | undefined): string =>
	SHARED_WITH_POPULATIONS.filter((population) => sharedWith?.includes(population)).join(",");

const UNSCOPED_VIEW_PERMISSION: Record<ReportType, ReportAnalyticsRbacPermissions> = {
	reviewReport: ReportAnalyticsRbacPermissions.ViewReport,
	employeeReport: ReportAnalyticsRbacPermissions.ViewEmployeeReport,
	goalReport: ReportAnalyticsRbacPermissions.ViewGoalReport,
	surveyReport: ReportAnalyticsRbacPermissions.ViewSurveyReport,
};

/** Mirrors reporting-webserver's pre-handler gate; anyone this rejects must send a subject filter or get a 401. */
export const canViewReportUnscoped = (
	userPermissions: string[] | undefined,
	reportType: ReportType | undefined
): boolean => {
	if (!userPermissions?.length) return false;
	if (userPermissions.includes(ReportAnalyticsRbacPermissions.ViewReport)) return true;
	const typePermission = reportType ? UNSCOPED_VIEW_PERMISSION[reportType] : undefined;
	return !!typePermission && userPermissions.includes(typePermission);
};
